import { EditablePersonInfo } from '@/data/Content/PersonInfo';
import { useForm } from '@/utils/useForm';
import { formatPhoneNumber, formatPhoneNumberValue } from '@/utils/InputPhone';
import { formatName } from '@/utils/InputName';
import { REG_EX } from '@/utils/address';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { Grid, Stack } from '@mui/material';
import { ChangeEvent, FC, FocusEvent, useMemo, useState } from 'react';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { AccountBlockedEmailSX } from '@/components/content/Account/styles/accountInformationForm';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { CompleteDataButtonSX } from './style';

interface CompleteDataFormProps {
	personInfo: EditablePersonInfo;
	onSubmit: (personInfo: EditablePersonInfo, skipAlert?: boolean) => Promise<void>;
	onSaved: () => Promise<unknown>;
	saving?: boolean;
}

type EditablePersonInfoKeys = keyof EditablePersonInfo;

const initialValuesError = {
	email1: false,
	firstName: false,
	lastName: false,
	lastNameMat: false,
	phone1: false,
};

export const CompleteDataForm: FC<CompleteDataFormProps> = ({
	personInfo,
	onSubmit,
	onSaved,
	saving,
}) => {
	const { values, handleSubmit, formRef, handleInputChange } = useForm(personInfo);
	const [error, setError] = useState(initialValuesError);

	const validateFields = (): boolean => {
		let isValid = true;
		const newError = { ...error };

		const cleanedPhoneNumber = values.phone1.replace(/\D/g, '');
		if (!cleanedPhoneNumber || cleanedPhoneNumber.length !== 10) {
			newError.phone1 = true;
			isValid = false;
		}

		if (!values.firstName?.trim()) {
			newError.firstName = true;
			isValid = false;
		}
		if (!values.lastName?.trim()) {
			newError.lastName = true;
			isValid = false;
		}
		if (!values.lastNameMat?.trim()) {
			newError.lastNameMat = true;
			isValid = false;
		}

		setError(newError);
		return isValid;
	};

	const handleFormSubmit = async () => {
		if (!validateFields()) return;

		const updatedPersonInfo = {
			...values,
			firstName: values.firstName.trim(),
			lastName: values.lastName.trim(),
			lastNameMat: values.lastNameMat?.trim(),
		};

		await onSubmit(updatedPersonInfo, true);
		await onSaved();
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name } = e.target;
		handleInputChange(e);
		setError((prev) => ({ ...prev, [name]: false }));
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		const { name } = e.target;
		const key = name as EditablePersonInfoKeys;
		if (!values[key]) {
			setError((prev) => ({ ...prev, [name]: true }));
		}
	};

	const helperText = useMemo(
		() => ({
			firstName: error.firstName ? 'Captura tu Nombre' : EMPTY_STRING,
			lastName: error.lastName ? 'Captura tu Apellido paterno' : EMPTY_STRING,
			lastNameMat: error.lastNameMat ? 'Captura tu Apellido materno' : EMPTY_STRING,
			phone1: error.phone1
				? values.phone1.length === 0
					? 'Captura tu Teléfono'
					: 'Teléfono inválido'
				: EMPTY_STRING,
		}),
		[error, values.phone1]
	);

	return (
		<Stack component="form" noValidate ref={formRef} onSubmit={handleSubmit(handleFormSubmit)}>
			<Grid container spacing={2} marginBottom={2}>
				<Grid item xs={12}>
					<DpOutlinedInput
						id="complete-data-email"
						data-testid="complete-data-email"
						name="email1"
						type="email"
						label="Correo electrónico"
						value={values.email1}
						inputProps={{ readOnly: true, tabIndex: -1 }}
						fullWidth
						autoComplete="email"
						sx={AccountBlockedEmailSX}
						disabled
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="complete-data-firstName"
						data-testid="complete-data-firstName"
						name="firstName"
						label="Nombre"
						value={values.firstName}
						inputProps={{ maxLength: 50 }}
						fullWidth
						error={error.firstName}
						autoComplete="given-name"
						helperText={helperText.firstName}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="complete-data-lastName"
						data-testid="complete-data-lastName"
						name="lastName"
						label="Apellido paterno"
						value={values.lastName}
						inputProps={{ maxLength: 50 }}
						fullWidth
						error={error.lastName}
						autoComplete="family-name"
						helperText={helperText.lastName}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="complete-data-lastNameMat"
						data-testid="complete-data-lastNameMat"
						name="lastNameMat"
						label="Apellido materno"
						value={values.lastNameMat}
						inputProps={{ maxLength: 50 }}
						fullWidth
						error={error.lastNameMat}
						autoComplete="family-name"
						helperText={helperText.lastNameMat}
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatName(event, onChange)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<DpOutlinedInput
						required
						id="complete-data-phone1"
						name="phone1"
						type="tel"
						data-testid="complete-data-phone1"
						label="Teléfono"
						value={formatPhoneNumberValue(values.phone1)}
						error={error.phone1}
						helperText={helperText.phone1}
						inputProps={{ maxLength: 12, pattern: REG_EX.PHONE.source }}
						fullWidth
						autoComplete="tel"
						onBlur={handleBlur}
						onChange={(event: ChangeEvent<HTMLInputElement>) => formatPhoneNumber(event, onChange)}
					/>
				</Grid>
			</Grid>

			<DpButton
				variant="primary"
				type="submit"
				data-testid="button-complete-data-save"
				id="button-complete-data-save"
				fullWidth
				sx={CompleteDataButtonSX}
				disabled={saving}
			>
				{'Guardar y continuar'}
			</DpButton>
		</Stack>
	);
};
